import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/hooks/useTranslation';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import Avatar from '@components/ui/Avatar/Avatar';
import Tabs from '@components/ui/Tabs/Tabs';
import type { TabItem } from '@components/ui/Tabs/Tabs';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { getEmployees, activateEmployee, deleteEmployee, getEmployeeCounts, provisionEmployeeAccess } from '@/services/employeeService';
import { getTenants } from '@/services/tenantService';
import { getLegalEntities } from '@/services/legalEntityService';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useCountries } from '@/hooks/useCountries';
import type { EmployeeDto } from '@/types/employee';
import type { FilterFieldConfig } from '@/types/filters';
import { WorkpayApiError } from '@lib/apiEnvelope';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/employees/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { sortBy: 'created', sortDir: 'desc', tab: 'ACTIVE' }),
  component: AdminEmployeesPage,
});

function statusBadgeClass(status: EmployeeDto['status'], styles: Record<string, string>): string {
  switch (status) {
    case 'ACTIVE':
      return styles.badgeActive!;
    case 'PENDING_CONTRACT':
      return styles.badgeWarning!;
    case 'PENDING_ONBOARDING':
    case 'ACTIVATION_PENDING':
      return styles.badgeInfo!;
    case 'INACTIVE':
      return styles.badgeInactive!;
  }
}

function StatusBadge({ status }: { status: EmployeeDto['status'] }) {
  return (
    <span className={`${styles.badge} ${statusBadgeClass(status, styles)}`}>
      {toTitleCase(status)}
    </span>
  );
}

function AdminEmployeesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam, tab } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;
  const activeTab = tab ?? 'ACTIVE';

  const { data: countsData } = useQuery({
    queryKey: ['allEmployeeCounts'],
    queryFn: getEmployeeCounts,
  });

  const tabCounts = useMemo(() => {
    if (!countsData) return undefined;
    const total =
      (countsData.ACTIVE ?? 0) +
      (countsData.PENDING_CONTRACT ?? 0) +
      (countsData.PENDING_ONBOARDING ?? 0) +
      (countsData.ACTIVATION_PENDING ?? 0) +
      (countsData.INACTIVE ?? 0);
    return {
      ACTIVE: countsData.ACTIVE ?? 0,
      PENDING_CONTRACT: countsData.PENDING_CONTRACT ?? 0,
      PENDING_ONBOARDING: countsData.PENDING_ONBOARDING ?? 0,
      ACTIVATION_PENDING: countsData.ACTIVATION_PENDING ?? 0,
      INACTIVE: countsData.INACTIVE ?? 0,
      ALL: total,
    };
  }, [countsData]);

  const statusTabs = useMemo<TabItem[]>(
    () => [
      { id: 'ACTIVE', label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')) },
      { id: 'PENDING_CONTRACT', label: toTitleCase(t('K_STATUS_PENDING_CONTRACT', 'Pending Contract')) },
      { id: 'PENDING_ONBOARDING', label: toTitleCase(t('K_STATUS_PENDING_ONBOARDING', 'Pending Onboarding')) },
      { id: 'ACTIVATION_PENDING', label: toTitleCase(t('K_STATUS_ACTIVATION_PENDING', 'Activation Pending')) },
      { id: 'INACTIVE', label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')) },
      { id: 'ALL', label: t('K_STATUS_ALL', 'All') },
    ],
    [t],
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      void navigate({
        to: '/w/employees',
        search: {
          tab: tabId,
          page: 0,
          size: urlSize,
          sortBy: 'created',
          sortDir: 'desc' as const,
        },
        replace: true,
      });
    },
    [navigate, urlSize],
  );

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Resolve country names
  const { countries } = useCountries();
  const countryMap = useMemo(() => new Map(countries.map((c) => [c.id, c.name])), [countries]);

  // Resolve tenant names (admin sees employees across all tenants)
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants', { size: 100 }],
    queryFn: () => getTenants({ size: 100 }),
  });
  const tenantMap = useMemo(
    () => new Map((tenantsData?.content ?? []).map((tw) => [tw.tenant.id, tw.tenant.name])),
    [tenantsData],
  );
  const tenantOptions = useMemo(
    () => (tenantsData?.content ?? []).map((tw) => ({ label: tw.tenant.name, value: tw.tenant.id })),
    [tenantsData],
  );

  // Resolve employment type names (admin has EMPLOYMENT_TYPES_VIEW)
  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', 'all'],
    queryFn: () => getEmploymentTypes({ size: 500 }),
  });
  const employmentTypeMap = useMemo(
    () => new Map((employmentTypesData?.content ?? []).map((et) => [et.id, et.name])),
    [employmentTypesData],
  );
  const employmentTypeOptions = useMemo(
    () => (employmentTypesData?.content ?? []).map((et) => ({ label: et.name, value: et.id })),
    [employmentTypesData],
  );

  // Resolve legal entity names
  const { data: legalEntitiesData } = useQuery({
    queryKey: ['legalEntities', { size: 100 }],
    queryFn: () => getLegalEntities({ size: 100 }),
  });
  const legalEntityMap = useMemo(
    () => new Map((legalEntitiesData?.content ?? []).map((le) => [le.id, le.name])),
    [legalEntitiesData],
  );
  const legalEntityOptions = useMemo(
    () => (legalEntitiesData?.content ?? []).map((le) => ({ label: le.name, value: le.id })),
    [legalEntitiesData],
  );

  // Filter field configs — ordered to match table columns
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'tenantId',
        label: t('K_FILTER_TENANT', 'Client'),
        type: 'select',
        options: tenantOptions,
        placeholder: t('K_PLACEHOLDER_SELECT_TENANT', 'Select client...'),
      },
      {
        fieldKey: 'countryId',
        label: t('K_FILTER_COUNTRY', 'Country'),
        type: 'select',
        options: countries.map((c) => ({ label: c.name, value: c.id })),
      },
      {
        fieldKey: 'employmentTypeId',
        label: t('K_FILTER_EMPLOYMENT_TYPE', 'Employment Type'),
        type: 'select',
        options: employmentTypeOptions,
        placeholder: t('K_PLACEHOLDER_SELECT_EMPLOYMENT_TYPE', 'Select employment type...'),
      },
      {
        fieldKey: 'contractType',
        label: t('K_FILTER_CONTRACT_TYPE', 'Contract Type'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_CONTRACT_TYPE', 'Search contract type...'),
      },
      {
        fieldKey: 'startDate',
        label: t('K_FILTER_START_DATE', 'Start Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
      {
        fieldKey: 'legalEntityId',
        label: t('K_FILTER_LEGAL_ENTITY', 'Legal Entity'),
        type: 'select',
        options: legalEntityOptions,
        placeholder: t('K_PLACEHOLDER_SELECT_LEGAL_ENTITY', 'Select legal entity...'),
      },
    ],
    [t, countries, tenantOptions, employmentTypeOptions, legalEntityOptions],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/employees',
    filtersParam,
    fields: filterFields,
  });

  const [activateItem, setActivateItem] = useState<EmployeeDto | null>(null);
  const [deactivateItem, setDeactivateItem] = useState<EmployeeDto | null>(null);
  const [provisionAccessItem, setProvisionAccessItem] = useState<EmployeeDto | null>(null);
  const audit = useAuditTrigger();
  // Pin the opener identity so row-action callbacks don't re-create on modal
  // open/close (which would re-render every row's actions cell).
  const auditOpen = audit.open;

  const activateMutation = useMutation({
    mutationFn: (id: string) => activateEmployee(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      setActivateItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setActivateItem(null);
    },
  });

  const provisionAccessMutation = useMutation({
    mutationFn: (id: string) => provisionEmployeeAccess(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      setProvisionAccessItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
      setProvisionAccessItem(null);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deleteEmployee(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['allEmployees'] });
      void queryClient.invalidateQueries({ queryKey: ['allEmployeeCounts'] });
      void queryClient.invalidateQueries({ queryKey: ['employeeCounts'] });
      setDeactivateItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setDeactivateItem(null);
    },
  });

  const columns = useMemo<DataTableColumn<EmployeeDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'firstName',
        width: '220px',
        cell: (item) => {
          const displayName = item.preferredName || `${item.firstName} ${item.lastName}`;
          return (
            <span className={styles.nameCell} title={displayName}>
              <Avatar
                name={displayName}
                size="xs"
                color="blue"
                {...(item.profileImageUrl ? { src: item.profileImageUrl } : {})}
              />
              <span className={styles.cellPrimary}>{displayName}</span>
            </span>
          );
        },
      },
      {
        id: 'workEmail',
        header: t('K_COLUMN_EMAIL', 'Email'),
        cell: (item) => item.workEmail || '-',
      },
      {
        id: 'tenant',
        header: t('K_COLUMN_TENANT', 'Client'),
        cell: (item) => tenantMap.get(item.tenantId) ?? '-',
      },
      {
        id: 'country',
        header: t('K_COLUMN_COUNTRY', 'Country'),
        cell: (item) => countryMap.get(item.countryId) ?? '-',
      },
      {
        id: 'jobTitle',
        header: t('K_COLUMN_JOB_TITLE', 'Job Title'),
        cell: (item) => item.jobTitle?.name ?? '-',
      },
      {
        id: 'employmentType',
        header: t('K_COLUMN_EMPLOYMENT_TYPE', 'Employment Type'),
        cell: (item) => employmentTypeMap.get(item.employmentTypeId) ?? item.employmentType ?? '-',
      },
      {
        id: 'contractType',
        header: t('K_COLUMN_CONTRACT_TYPE', 'Contract Type'),
        cell: (item) => item.contractType || '-',
      },
      {
        id: 'startDate',
        header: t('K_COLUMN_START_DATE', 'Start Date'),
        sortable: true,
        sortKey: 'startDate',
        cell: (item) =>
          item.startDate
            ? new Date(item.startDate).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '-',
      },
      {
        id: 'legalEntity',
        header: t('K_COLUMN_LEGAL_ENTITY', 'Legal Entity'),
        // Read embedded `legalEntity` from the BE list response (enriched via platform-service bulkResolve).
        cell: (item) => item.legalEntity?.name ?? '-',
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
      {
        id: 'created',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdOn',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.createdOn).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'updated',
        header: t('K_COLUMN_UPDATED', 'Updated'),
        sortable: true,
        sortKey: 'updatedOn',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.updatedOn).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [countryMap, tenantMap, employmentTypeMap, legalEntityMap, t],
  );

  const rowActions = useCallback((item: EmployeeDto) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('EMPLOYEES_UPDATE') && item.status !== 'INACTIVE') {
      actions.push({
        id: 'edit',
        label: t('K_EDIT', 'Edit'),
        onClick: () =>
          navigate({
            to: '/w/employees/profile/$employeeId',
            params: { employeeId: item.id },
            search: { edit: true },
          }),
      });
    }
    // Send onboarding link is valid only on PENDING_ONBOARDING (BE rejects other statuses
     // with 409). Idempotent: first call provisions Keycloak + sends email; subsequent calls
     // re-issue the email and invalidate the prior link.
    const canProvisionAccess =
      hasPermission('EMPLOYEES_UPDATE') && item.status === 'PENDING_ONBOARDING';
    if (canProvisionAccess) {
      actions.push({
        id: 'sendOnboardingLink',
        label: t('K_BUTTON_SEND_ONBOARDING_LINK', 'Send Onboarding Link'),
        onClick: () => setProvisionAccessItem(item),
      });
    }
    // Backend rejects activate on PENDING_CONTRACT (contract must be signed first) and on ACTIVE/INACTIVE.
    const canActivate = hasPermission('EMPLOYEES_UPDATE') &&
      (item.status === 'PENDING_ONBOARDING' || item.status === 'ACTIVATION_PENDING');
    if (canActivate) {
      actions.push({
        id: 'activate',
        label: t('K_BUTTON_ACTIVATE', 'Activate'),
        onClick: () => setActivateItem(item),
      });
    }
    // Contracts are only meaningful pre-signing. Once the contract-signed webhook has fired,
    // the employee has left PENDING_CONTRACT and a new envelope would bypass the lifecycle.
    const canGenerateContract =
      hasPermission('ENVELOPES_CREATE') && item.status === 'PENDING_CONTRACT';
    if (canGenerateContract) {
      actions.push({
        id: 'generate-contract',
        label: t('K_DS_ACTION_GENERATE_CONTRACT', 'Generate Contract'),
        onClick: () =>
          navigate({
            to: '/w/signing-requests/new',
            search: { employeeId: item.id },
          }),
      });
    }
    if (item.tenantId) {
      actions.push({
        id: 'viewTenant',
        label: t('K_BUTTON_VIEW_TENANT', 'View Client'),
        onClick: () =>
          navigate({
            to: '/w/dashboard/profile/$tenantId',
            params: { tenantId: item.tenantId },
            search: {},
          }),
      });
    }
    if (item.legalEntityId) {
      actions.push({
        id: 'viewLegalEntity',
        label: t('K_BUTTON_VIEW_LEGAL_ENTITY', 'View Legal Entity'),
        onClick: () =>
          navigate({
            to: '/w/entities/profile/$entityId',
            params: { entityId: item.legalEntityId! },
            search: {},
          }),
      });
    }
    const canDeactivate = hasPermission('EMPLOYEES_DELETE') && item.status === 'ACTIVE';
    if (canDeactivate) {
      actions.push({
        id: 'deactivate',
        label: t('K_DEACTIVATE', 'Deactivate'),
        onClick: () => setDeactivateItem(item),
      });
    }
    if (hasPermission('AUDIT_READ')) {
      const displayName = item.preferredName || `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim();
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(item.id, displayName || undefined),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, hasPermission, navigate, auditOpen]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/employees',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/employees',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/employees',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const mergedPayload = useMemo(() => {
    const baseFilters = (filterPayload?.filters ?? {}) as Record<string, unknown>;
    if (activeTab === 'ALL') return filterPayload;
    return {
      ...filterPayload,
      filters: { ...baseFilters, status: activeTab },
    };
  }, [filterPayload, activeTab]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['allEmployees', { page, size, sort: sortString, filterPayload: mergedPayload }],
    queryFn: () => getEmployees({ page, size, sort: sortString }, mergedPayload),
  });

  const employees = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: EmployeeDto) => item.id, []);

  const handleRowClick = useCallback(
    (item: EmployeeDto) => {
      void navigate({ to: '/w/employees/profile/$employeeId', params: { employeeId: item.id } });
    },
    [navigate],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_EMPLOYEES_ADMIN', 'No employees yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_NO_EMPLOYEES_ADMIN_DESC', 'Employees will appear here once clients add them.')}
          </p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_EMPLOYEES', 'Employees')}</h1>
          <p className={styles.subtitle}>
            {t('K_PAGE_SUBTITLE_ALL_EMPLOYEES', 'View all employees across clients')}
          </p>
        </div>
      </div>

      <Tabs
        tabs={statusTabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={tabCounts}
      />

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={employees}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        loadingText={t('K_LOADING_EMPLOYEES', 'Loading employees...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="All employees list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <AlertModal
        variant="warning"
        title={t('K_MODAL_ACTIVATE_EMPLOYEE', 'Activate Employee')}
        description={t('K_CONFIRM_ACTIVATE_EMPLOYEE', 'Are you sure you want to activate "{name}"? This will change the employee status to Active.').replace('{name}', activateItem ? (activateItem.preferredName || `${activateItem.firstName} ${activateItem.lastName}`) : '')}
        isOpen={activateItem !== null}
        onClose={() => setActivateItem(null)}
        primaryLabel={t('K_BUTTON_ACTIVATE', 'Activate')}
        onPrimaryClick={() => {
          if (activateItem) activateMutation.mutate(activateItem.id);
        }}
        loading={activateMutation.isPending}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.EMPLOYEE, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />

      <AlertModal
        variant="warning"
        title={t('K_MODAL_SEND_ONBOARDING_LINK', 'Send Onboarding Link')}
        description={t(
          'K_CONFIRM_SEND_ONBOARDING_LINK',
          'Send the onboarding link to "{name}"? They will receive an email with a link to set their password and finish onboarding. Any previous link will be invalidated.',
        ).replace(
          '{name}',
          provisionAccessItem
            ? provisionAccessItem.preferredName || `${provisionAccessItem.firstName} ${provisionAccessItem.lastName}`
            : '',
        )}
        isOpen={provisionAccessItem !== null}
        onClose={() => setProvisionAccessItem(null)}
        primaryLabel={t('K_BUTTON_SEND_ONBOARDING_LINK', 'Send Onboarding Link')}
        onPrimaryClick={() => {
          if (provisionAccessItem) provisionAccessMutation.mutate(provisionAccessItem.id);
        }}
        loading={provisionAccessMutation.isPending}
      />

      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_EMPLOYEE', 'Deactivate Employee')}
        description={t('K_CONFIRM_DEACTIVATE_EMPLOYEE', 'Are you sure you want to deactivate "{name}"? The employee will no longer be active.').replace('{name}', deactivateItem ? (deactivateItem.preferredName || `${deactivateItem.firstName} ${deactivateItem.lastName}`) : '')}
        isOpen={deactivateItem !== null}
        onClose={() => setDeactivateItem(null)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => {
          if (deactivateItem) deactivateMutation.mutate(deactivateItem.id);
        }}
        loading={deactivateMutation.isPending}
      />
    </div>
  );
}
